import {
  cloneReplay,
  createReplay,
  hashDeterministicState,
  recordReplayFrame,
  type SimulationReplay,
  type SimulationSnapshot,
} from "./determinism";
import { COMBO_RESET_FRAMES } from "./combo-scaling";
import type { InputFrame } from "./game-types";
import {
  FIXED_STEP,
  GameSimulation,
  LEDGE_GRAB_INVULNERABILITY,
  LEDGE_REGRAB_COOLDOWN,
  RECOVERY_INPUT_BUFFER_FRAMES,
} from "./simulation";

type LabMode = "idle" | "recording" | "replaying";

interface CombatLabApi {
  startRecording(): boolean;
  stopRecording(): SimulationReplay | null;
  replayLatest(): boolean;
  toggleCpu(): boolean;
  latestReplay(): SimulationReplay | null;
  serializeLatest(): string | null;
  stateHash(): string;
}

function normalizePausedStatus(snapshot: SimulationSnapshot): SimulationSnapshot {
  const normalized = structuredClone(snapshot);
  if (normalized.status === "paused") {
    normalized.status = normalized.countdown > 0 ? "countdown" : "playing";
  }
  return normalized;
}

function normalizedHash(simulation: GameSimulation): string {
  return hashDeterministicState(
    normalizePausedStatus(simulation.captureSnapshot()),
  );
}

function formatNumber(value: number): string {
  return Number.isFinite(value) ? value.toFixed(1) : "NaN";
}

function summarizeInput(input: InputFrame): string {
  const intents: string[] = [];
  if (input.moveX !== 0) {
    intents.push(input.moveX < 0 ? "LEFT" : "RIGHT");
  }
  if (input.jumpPressed) {
    intents.push("JUMP");
  }
  if (input.attackPressed) {
    intents.push("ATTACK");
  }
  if (input.specialPressed) {
    intents.push("SPECIAL");
  }
  if (input.grabPressed) {
    intents.push("GRAB");
  }
  if (input.shieldPressed || input.shieldHeld) {
    intents.push("SHIELD");
  }
  if (input.dashHeld) {
    intents.push("DASH");
  }
  if (input.downHeld) {
    intents.push("DOWN");
  }
  return intents.join("+") || "NONE";
}

export class CombatLab {
  private mode: LabMode = "idle";
  private recording: SimulationReplay | null = null;
  private latest: SimulationReplay | null = null;
  private replayCursor = 0;
  private verdict = "NO CAPTURE";
  private lastIntent = "NONE";
  private readonly panel: HTMLElement;
  private readonly statusLine: HTMLElement;
  private readonly dogLine: HTMLElement;
  private readonly tigerLine: HTMLElement;

  constructor(
    private readonly simulation: GameSimulation,
    host: HTMLElement,
  ) {
    this.panel = document.createElement("aside");
    this.panel.className = "combat-lab";
    this.panel.setAttribute("aria-label", "Combat recovery lab");

    const heading = document.createElement("strong");
    heading.textContent = "COMBAT RECOVERY LAB";
    const controls = document.createElement("span");
    controls.className = "combat-lab__controls";
    controls.textContent =
      "` RECORD / 0 REPLAY / ESC PAUSE / . STEP / [ RESET / L LEDGE / C CPU / -/+ DAMAGE";

    this.statusLine = document.createElement("code");
    this.dogLine = document.createElement("code");
    this.tigerLine = document.createElement("code");
    this.panel.append(
      heading,
      controls,
      this.statusLine,
      this.dogLine,
      this.tigerLine,
    );
    host.append(this.panel);
    this.installApi();
    this.render();
  }

  resetForMatch(): void {
    this.mode = "idle";
    this.recording = null;
    this.replayCursor = 0;
    this.lastIntent = "NONE";
    this.verdict = "AUTO CAPTURE ARMED";
    this.startRecording("match-start");
  }

  startRecording(label = "manual-capture"): boolean {
    if (this.mode === "replaying") {
      return false;
    }
    if (
      this.simulation.status !== "playing" &&
      this.simulation.status !== "countdown" &&
      this.simulation.status !== "paused"
    ) {
      this.verdict = "START A MATCH FIRST";
      return false;
    }

    const initialState = normalizePausedStatus(
      this.simulation.captureSnapshot(),
    );
    this.recording = createReplay(
      initialState,
      `${label}-f${this.simulation.frameNumber}`,
    );
    this.mode = "recording";
    this.verdict = "CAPTURING";
    return true;
  }

  stopRecording(): SimulationReplay | null {
    if (this.mode !== "recording" || !this.recording) {
      return this.latest ? cloneReplay(this.latest) : null;
    }

    this.recording.finalHash = normalizedHash(this.simulation);
    this.latest = cloneReplay(this.recording);
    this.recording = null;
    this.mode = "idle";
    this.verdict = `CAPTURED ${this.latest.frames.length} FRAMES`;
    console.info("[combat-lab] replay captured", cloneReplay(this.latest));
    return cloneReplay(this.latest);
  }

  toggleRecording(): void {
    if (this.mode === "recording") {
      this.stopRecording();
    } else {
      this.startRecording();
    }
  }

  replayLatest(): boolean {
    if (this.mode === "recording") {
      this.stopRecording();
    }
    if (!this.latest || this.latest.frames.length === 0) {
      this.verdict = "NO REPLAY FRAMES";
      return false;
    }

    this.simulation.restoreSnapshot(
      normalizePausedStatus(this.latest.initialState),
    );
    this.replayCursor = 0;
    this.mode = "replaying";
    this.verdict = "REPLAYING";
    return true;
  }

  isReplaying(): boolean {
    return this.mode === "replaying";
  }

  toggleCpu(): boolean {
    if (this.mode === "replaying") {
      return this.simulation.cpuEnabled;
    }
    if (this.mode === "recording") {
      this.stopRecording();
    }
    this.simulation.cpuEnabled = !this.simulation.cpuEnabled;
    this.startRecording(this.simulation.cpuEnabled ? "cpu-on" : "cpu-off");
    return this.simulation.cpuEnabled;
  }

  stepSimulation(liveInput: InputFrame, pausedStep = false): void {
    let input = liveInput;
    let shouldStepPaused = pausedStep;

    if (this.mode === "replaying") {
      const frame = this.latest?.frames[this.replayCursor];
      if (!frame) {
        this.finishReplay();
        return;
      }
      input = { ...frame.input };
      shouldStepPaused = frame.pausedStep;
    } else if (this.mode === "recording" && this.recording) {
      recordReplayFrame(this.recording, liveInput, pausedStep);
    }
    this.lastIntent = summarizeInput(input);

    if (shouldStepPaused) {
      if (this.simulation.status !== "paused") {
        this.simulation.pause();
      }
      this.simulation.stepPausedFrame(input);
    } else {
      if (this.mode === "replaying" && this.simulation.status === "paused") {
        this.simulation.resume();
      }
      this.simulation.step(FIXED_STEP, input);
    }

    if (this.mode === "replaying") {
      this.replayCursor += 1;
      if (this.replayCursor >= (this.latest?.frames.length ?? 0)) {
        this.finishReplay();
      }
    }
  }

  render(): void {
    const replayLength =
      this.mode === "recording"
        ? this.recording?.frames.length ?? 0
        : this.latest?.frames.length ?? 0;
    const cursor =
      this.mode === "replaying" ? `${this.replayCursor}/${replayLength}` : replayLength;
    this.statusLine.textContent =
      `MODE ${this.mode.toUpperCase()}  FRAME ${this.simulation.frameNumber}  ` +
      `CPU ${this.simulation.cpuEnabled ? "ON" : "OFF"}  REPLAY ${cursor}  ` +
      `HITSTOP ${Math.ceil(this.simulation.hitStop / FIXED_STEP)}  ` +
      `INPUT ${this.lastIntent}  BUFFER DEF ${RECOVERY_INPUT_BUFFER_FRAMES}F  ` +
      `COMBO RESET ${COMBO_RESET_FRAMES}F  ` +
      `LEDGE I${Math.round(LEDGE_GRAB_INVULNERABILITY / FIXED_STEP)}F` +
      `/CD${Math.round(LEDGE_REGRAB_COOLDOWN / FIXED_STEP)}F  ` +
      `${this.formatFrameAdvantage()}  ` +
      `HASH ${normalizedHash(this.simulation)}  ${this.verdict}`;

    this.dogLine.textContent = this.formatFighter("dog");
    this.tigerLine.textContent = this.formatFighter("tiger");
  }

  latestReplay(): SimulationReplay | null {
    return this.latest ? cloneReplay(this.latest) : null;
  }

  private finishReplay(): void {
    const expected = this.latest?.finalHash;
    const actual = normalizedHash(this.simulation);
    this.mode = "idle";
    this.verdict =
      expected === actual
        ? `REPLAY VERIFIED ${actual}`
        : `REPLAY MISMATCH ${expected ?? "NO HASH"} != ${actual}`;
  }

  private formatFighter(id: "dog" | "tiger"): string {
    const fighter = this.simulation.fighters.find(
      (candidate) => candidate.id === id,
    );
    if (!fighter) {
      return `${id.toUpperCase()} NOT ACTIVE`;
    }
    const diagnostic = this.simulation.getFighterDiagnostics(id);
    return (
      `${id.toUpperCase()} ${diagnostic.state.toUpperCase()}` +
      `@${diagnostic.stateAgeFrames} OWNER ${diagnostic.motionOwner}` +
      `  P ${formatNumber(diagnostic.position.x)},${formatNumber(diagnostic.position.y)}` +
      `  V ${formatNumber(diagnostic.velocity.x)},${formatNumber(diagnostic.velocity.y)}` +
      `  G ${diagnostic.grounded ? diagnostic.groundPlatform ?? "YES" : "AIR"}` +
      `  ACT ${diagnostic.actionFramesRemaining}` +
      `  STUN ${diagnostic.hitstunFramesRemaining}` +
      `  I ${diagnostic.invulnerabilityFramesRemaining}` +
      `  BUF ${diagnostic.bufferedAction?.toUpperCase() ?? "-"}:${diagnostic.bufferedActionFrames}` +
      `  CMB ${diagnostic.comboCount}` +
      `@${Math.round(diagnostic.comboDamageScale * 100)}%` +
      `/K${Math.round(diagnostic.comboKnockbackScale * 100)}%` +
      `/H${Math.round(diagnostic.comboHitstunScale * 100)}%` +
      ` R${diagnostic.comboRecoveryFrames}` +
      `  JMP ${diagnostic.jumpsRemaining}` +
      `  LEDGE ${diagnostic.ledgeId ?? "-"}:${diagnostic.ledgeHangFramesRemaining}` +
      `/CD${diagnostic.ledgeGrabCooldownFrames}` +
      `  HOLD ${diagnostic.holding ?? "-"}/${diagnostic.grabbedBy ?? "-"}`
    );
  }

  private formatFrameAdvantage(): string {
    const dog = this.simulation.getFighterDiagnostics("dog");
    const tiger = this.simulation.getFighterDiagnostics("tiger");
    if (dog.hitstunFramesRemaining > 0 && tiger.hitstunFramesRemaining > 0) {
      return "ADV TRADE";
    }
    if (dog.hitstunFramesRemaining > 0) {
      const frames =
        dog.hitstunFramesRemaining - tiger.actionFramesRemaining;
      return `ADV TIGER ${frames >= 0 ? "+" : ""}${frames}`;
    }
    if (tiger.hitstunFramesRemaining > 0) {
      const frames =
        tiger.hitstunFramesRemaining - dog.actionFramesRemaining;
      return `ADV DOG ${frames >= 0 ? "+" : ""}${frames}`;
    }
    return "ADV NEUTRAL";
  }

  private installApi(): void {
    const api: CombatLabApi = {
      startRecording: () => this.startRecording(),
      stopRecording: () => this.stopRecording(),
      replayLatest: () => this.replayLatest(),
      toggleCpu: () => this.toggleCpu(),
      latestReplay: () => this.latestReplay(),
      serializeLatest: () =>
        this.latest ? JSON.stringify(this.latest, null, 2) : null,
      stateHash: () => normalizedHash(this.simulation),
    };
    (
      window as Window & {
        __COOL_ANIMALS_COMBAT_LAB__?: CombatLabApi;
      }
    ).__COOL_ANIMALS_COMBAT_LAB__ = api;
  }
}
